import _ from 'lodash';

import { evalSSEexpression, evaluateConditions, getValueIndex } from './evaluation.utils';
import {
  ENTITY_DATA_PREFIX,
  SS_CONTROL_TYPE,
  SS_FUNCTIONS_REPLACEMENT,
} from '../constants/form-manager.constants';
import { ONE_TO_MANY } from '../constants/runtime.constants';
/**
 * Get exact path to an entity at the entityDataStore, applying the gridArrIndex
 * @param {string} entityName V_SS_AUDIT-V_SS_AUDIT_CRITERIA-V_SS_AUDIT_CRI_SECTION
 * @param {*} index [0, 1]
 * @param {*} entityDefinition
 * @returns ["V_SS_AUDIT", "V_SS_AUDIT_CRITERIA", 0, "V_SS_AUDIT_CRI_SECTION", 1]
 */
export const getDataPath = (entityName, index = [0], entityDefinition) => {
  if (_.isNil(entityName)) {
    return [];
  }
  const mainEntity = Object.keys(entityDefinition)[0];

  if (entityName === 'V_ENU_NONPERSIS_USER_DATA') {
    return [mainEntity, entityName];
  }

  // entityDefinition and entityData has the full address of the entity
  // so we need to append the entity name of each split with the previous split
  const split = entityName.split('-');
  const entities = split.map((entity, i) => {
    let currentPath = '';
    for (let x = 0; x < i; x++) {
      currentPath += split[x] + '-';
    }
    return (currentPath += entity);
  });

  // create an array containing the path with index for the given controller
  // i.e: ["V_SS_NC", "V_SS_NC-V_SS_NC_PART", 0, "V_SS_NC-V_SS_NC_PART-V_SS_NC_PART_LOT", 1]
  return entities.reduce((acc, entity, i) => {
    return entity === mainEntity ? [entity] : [...acc, entity, index[i - 1]];
  }, []);
};

/**
 * Get all values of the datamember object disregard of its index
 * @param {Object} data data object from store
 * @param {string} dataMember "V_SS_AUDIT-V_SS_AUDIT_CRITERIA-V_SS_AUDIT_CRI_SECTION-V_SS_AUDIT_QUESTION"
 * @returns Array of all possible values from given entity
 */
export function getFullDataSetByDataMember(data, dataMember) {
  if (_.isNil(data) || typeof dataMember !== 'string') {
    return [];
  }

  const entityType = dataMember.split('-')[0];
  const entityData = _.get(data, `entityDataStore.entityData.${entityType}`);

  if (entityType === dataMember || !entityData) {
    return entityData;
  }

  const _getDataByDataMember = (_data, result = []) => {
    let matchingKey = Object.keys(_data).find((key) => dataMember === key);

    if (_.isNil(matchingKey)) {
      const dataMemberArray = dataMember.split('-');
      matchingKey = Object.keys(_data).find((key) =>
        key.split('-').every((subKey) => dataMemberArray.includes(subKey))
      );
    }

    if (!matchingKey) {
      return result;
    }

    const value = _data[matchingKey];
    if (!Array.isArray(value)) {
      return result;
    }

    if (matchingKey === dataMember) {
      return result.concat(value);
    }
    return result.concat(_.flatten(value.map((v) => _getDataByDataMember(v))));
  };

  return _getDataByDataMember(entityData);
}

export const _find = (collection, key, value) => {
  for (const o of collection) {
    for (const [k, v] of Object.entries(o)) {
      if (k === key && v === value) {
        return o;
      }
      if (Array.isArray(v)) {
        const _o = _find(v, key, value);
        if (_o) {
          return _o;
        }
      }
    }
  }
};

export const testJSON = (text) => {
  if (typeof text !== 'string') {
    return false;
  }
  try {
    JSON.parse(text);
    return true;
  } catch (error) {
    return false;
  }
};

export const testExpression = (text) => {
  if (typeof text !== 'string') {
    return false;
  }
  return _.startsWith(text, 'eval||');
};

export const getPropTranslation = (entityDefinition, translations, entity) => {
  if (!entity) {
    return '';
  }

  const [datamember, column] = entity.split('.');

  if (!column) {
    return entity;
  }

  const entityType = Object.keys(entityDefinition)[0];
  const translationKey = getTranslationKey(entityDefinition, entityType, datamember, column);

  return translate(translations, translationKey);
};

const parseDataType = (dataType) => {
  switch (dataType) {
    case 'System.Byte':
    case 'System.Boolean': //default value for targetDataType for boolean form FD is 'xs:string'
    case 'System.String':
      return 'xs:string';

    case 'System.Int32':
      return 'xs:int';

    case 'System.DateTime': //default value for targetDataType form FD is 'resolver'
      return 'resolver';

    default:
      return 'resolver';
  }
};

export const getPropType = (entityDefinition, entity, returnKey, shouldParseDataType = false) => {
  if (!entity) {
    return '';
  }

  const [entityName, fieldName] = entity.split('.');

  if (!entityName || !fieldName) {
    return '';
  }

  const entityType = Object.keys(entityDefinition)[0];
  const args = [entityDefinition, entityType, entityName, fieldName];

  switch (returnKey) {
    case 'MaxLength': {
      return getMaxLength(...args);
    }

    case 'DataType': {
      const dataType = getDataType(...args);

      if (shouldParseDataType) {
        return parseDataType(dataType);
      }

      return dataType;
    }

    default:
      return '';
  }
};

export const equalityFn = (prev, next) => _.isEqual(prev, next);

export const getParentNode = (entityDefinition, entityName = '') => {
  if (entityName === 'V_ENU_NONPERSIS_USER_DATA') {
    return _.get(Object.values(entityDefinition)[0], entityName);
  }

  const path = [];

  entityName?.split('-').reduce((acc, curr, i) => {
    acc += `${i === 0 ? '' : '-'}${curr}`;
    path.push(acc);
    return acc;
  }, '');

  return _.get(entityDefinition, path);
};

export const findClosestParentIdByType = (formModel, controlId, type) => {
  const parentControl = Object.values(formModel).find(
    (control) => control.childrenNodes && control.childrenNodes.includes(controlId)
  );

  if (!parentControl) {
    return;
  }

  if (parentControl.controlType === type) {
    return parentControl.controlId;
  } else {
    return findClosestParentIdByType(formModel, parentControl.controlId, type);
  }
};

export const findClosestContainerParent = (formModel, controlId) => {
  const parentControl = Object.values(formModel).find(
    (control) => control.childrenNodes && control.childrenNodes.includes(controlId)
  );

  if (!parentControl) {
    return;
  }

  if (
    parentControl.controlType === SS_CONTROL_TYPE.SUB_GRID ||
    parentControl.controlType === SS_CONTROL_TYPE.REPEATING_PANEL
  ) {
    return parentControl.controlId;
  } else {
    return findClosestContainerParent(formModel, parentControl.controlId);
  }
};

export const findClosestParentWithEntityName = (formModel, entityType, id) => {
  if (!formModel[id]) {
    return;
  }
  const { parent } = formModel[id];
  const parentNode = formModel[parent];

  if (!parentNode) {
    return entityType;
  }
  if (parentNode.entityName) {
    return parentNode.entityName;
  }

  return findClosestParentWithEntityName(formModel, entityType, parent);
};

export const applyFilterToDataSource = (ssContext, dataSourceValues, filter) =>
  Array.isArray(dataSourceValues)
    ? dataSourceValues.filter((row) =>
        evaluateConditions({
          conditions: filter,
          ssContext,
          datasourceRow: row,
        })
      )
    : dataSourceValues;

/**
 * V_SS_NC-V_SS_NC_PART-V_SS_NC_PART_LOT -> V_SS_NC-V_SS_NC_PART
 */
export const getParentEntityName = (entityName) =>
  entityName
    .split('-')
    .slice(0, -1)
    .join('-');

export const getIndexForControlsInsideTable = (tableIndex, rowIndex, isParentlessTable) => {
  let gridArrIndex;

  if (isParentlessTable) {
    gridArrIndex = rowIndex;
  } else {
    // replace last element with current row index
    gridArrIndex = tableIndex.length > 1 ? [...tableIndex.slice(0, -1), rowIndex] : [rowIndex];
  }

  return gridArrIndex;
};

export const getOpitonsForPredefinedDataSource = (dataSource, dataSourceKey) =>
  dataSource.map(({ Id, Name, TranslatedName }) => ({
    id: Id,
    value: dataSourceKey === 'UserActionList' ? Name : Id,
    label: TranslatedName ?? Name,
  }));

// [V_SS_CAPA, V_SS_CAPA_ACTION, V_SS_CAPA_ACTION_RC] (idx=1) -> V_SS_CAPA-V_SS_CAPA_ACTION
// [V_SS_CAPA, V_SS_CAPA_ACTION, V_SS_CAPA_ACTION_RC] (idx=2) -> V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC
const aggregate = (arr, idx) => {
  if (arr.length === 1) {
    return arr[0];
  }

  let s = arr[0];

  for (let i = 1; i <= idx; i++) {
    s = `${s}-${arr[i]}`;
  }

  return s;
};

/**
 * Converts dataMember to dataMember access path
 *
 * V_SS_CAPA -> V_SS_CAPA
 * V_SS_CAPA-V_SS_CAPA_ACTION -> V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_ACTION
 * V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC -> V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_ACTION.V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC
 *
 * @param dataMember
 * @returns {string}
 */
const getDataMemberPathForEntityDefinition = (dataMember) => {
  if (_.isNil(dataMember)) {
    return '';
  }

  let xPath = '';
  const arr = dataMember.split('-');

  for (let idx = 0; idx < arr.length; idx++) {
    if (idx === 0) {
      // top entity section
      xPath += arr[0];
    } else {
      const subPath = aggregate(arr, idx);
      xPath += `.${subPath}`;
    }
  }

  return xPath;
};

const getEntityColumnPropByEntityDefinition = (propName) => (
  entityDefinition,
  entityType,
  dataMember,
  dataField
) => {
  let xPath =
    dataMember === 'V_ENU_NONPERSIS_USER_DATA'
      ? `${entityType}.${dataMember}`
      : getDataMemberPathForEntityDefinition(dataMember);

  xPath += `.columns.${dataField}.properties.${propName}`;
  return _.get(entityDefinition, xPath);
};

const getEntityPropByEntityDefinition = (propName) => (entityDefinition, dataMember) => {
  if (dataMember === 'V_ENU_NONPERSIS_USER_DATA') {
    let propValue;
    // eslint-disable-next-line no-unused-vars
    Object.entries(entityDefinition).forEach(([key, value]) => {
      // inside top entity
      const xPath = `${dataMember}.properties.${propName}`;
      const tmp = _.get(value, xPath);

      if (tmp) {
        propValue = tmp;
      }
    });
    return propValue;
  }

  const xPath = `${getDataMemberPathForEntityDefinition(dataMember)}.properties.${propName}`;
  return _.get(entityDefinition, xPath, null);
};

export const getFormatType = getEntityColumnPropByEntityDefinition('FormatType');

export const getTranslationKey = getEntityColumnPropByEntityDefinition('Caption');

export const getDataType = getEntityColumnPropByEntityDefinition('DataType');

export const getDefaultValueFromEntityDefinition = getEntityColumnPropByEntityDefinition(
  'DefaultValue'
);

const getMaxLength = getEntityColumnPropByEntityDefinition('MaxLength');

export const getEntityPrimaryKey = getEntityPropByEntityDefinition('PrimaryKey');

export const getEntityParentObjectKey = getEntityPropByEntityDefinition('ParentObjectKey');

export const getModifiedDateColumn = getEntityPropByEntityDefinition('ModifiedDateColumn');

export const getRelationShipKey = getEntityPropByEntityDefinition('RelationShipKey');

export const getParentRelationshipKey = ({ entityDefinition, dataMember }) => {
  let parentDataMember = '';
  let parentDataRelationshipKey = '';
  const dataMemberArray = dataMember.split('-');

  if (dataMemberArray.length > 1) {
    dataMemberArray.pop();
    parentDataMember = dataMemberArray.join('-');
    parentDataRelationshipKey = getRelationShipKey(entityDefinition, parentDataMember);
  }

  return { parentDataRelationshipKey, dataMemberArray };
};

const getAllDataMembers = (entityDefinition) => {
  const allDataMembers = [];
  const keysToExclude = ['columns', 'properties'];

  const _getAllDataMembers = (entityDefinition) => {
    for (const [key, value] of Object.entries(entityDefinition)) {
      if (!keysToExclude.includes(key)) {
        allDataMembers.push(key);

        if (Object.keys(value).length > 2) {
          // has sub entities
          _getAllDataMembers(value);
        }
      }
    }
  };

  _getAllDataMembers(entityDefinition);

  return allDataMembers;
};

const createEntityPropertyMapForAllDataMembers = (getPropFn) => (entityDefinition) => {
  const allDataMembers = getAllDataMembers(entityDefinition);

  return allDataMembers.reduce(
    (acc, dataMember) => ({
      ...acc,
      [dataMember]: getPropFn(entityDefinition, dataMember),
    }),
    {}
  );
};

export const createPrimaryKeysMap = createEntityPropertyMapForAllDataMembers(getEntityPrimaryKey);

export const createRelationShipKeysMap = createEntityPropertyMapForAllDataMembers(
  getRelationShipKey
);

// replaces functions' calls in rules
export const updateRulesScripts = (str, dataReplacement) => {
  let replacement = { ...SS_FUNCTIONS_REPLACEMENT };

  if (dataReplacement) {
    replacement = Object.fromEntries(Object.entries(replacement).map((a) => a.reverse()));
  }

  for (const key in replacement) {
    const value = replacement[key];
    str = str.split(key).join(value);
  }

  return str;
};

export const showAsteriskBefore = (label, controlIsInTable) => {
  const isInTable = _.defaultTo(controlIsInTable, false);
  const isLabelEmpty = _.isEmpty(label);

  return isLabelEmpty && isInTable;
};

export const getDecimalScale = (dataType, formatType, value) => {
  if (dataType !== 'System.Decimal') {
    return 0;
  }

  switch (formatType) {
    case 'Decimal2p':
    case 'Cost':
      return 2;
    case 'Decimal4p':
      return 4;
    default:
      return value?.toString().split('.')[1]?.length ?? 0;
  }
};

export const toArray = (node) => (_.isNil(node) ? [] : Array.isArray(node) ? node : [node]);

export const translate = (translations, key) => {
  if (translations) {
    if (translations.Form?.[key]) {
      return translations.Form[key];
    }
    if (translations.Entity?.[key]) {
      return translations.Entity[key];
    }
    if (translations.system?.[key]) {
      return translations.system[key];
    }
    if (translations.cached?.[key]) {
      return translations.cached[key];
    }
  }

  return key; // fallback
};

export const getEntityDataPathArr = (entityDataPath, data) => {
  let pathArr = [];
  const entityDataPathArr = entityDataPath.split('.');

  // loop over dataMember path depth
  entityDataPathArr.forEach((subPath, idx) => {
    if (idx === 0) {
      pathArr.push(subPath);
    } else {
      const newPathArr = [];

      pathArr.forEach((path) => {
        const dataMemberDataArr = _.get(data, `${path}.${subPath}`);

        if (Array.isArray(dataMemberDataArr)) {
          dataMemberDataArr.forEach((el, elIdx) => {
            newPathArr.push(`${path}.${subPath}[${elIdx}]`);
          });
        }
      });

      pathArr = newPathArr;
    }
  });

  return pathArr;
};

const getGridArrIndexBasedOnRelationshipKey = (gridArrIndex, dataMember, entityDefinition) => {
  if (_.isNil(gridArrIndex)) {
    return;
  }

  if (_.isNil(entityDefinition)) {
    // If this happens we should debug and look at the call stack to see why entityDefinition is coming as null and pass it
    // Note: When we leave a form, it is normal to get this log because the formDesigner store is cleared out
    // eslint-disable-next-line no-console
    console.error(
      'getDataMemberPath() Error:',
      'Entity Definition should be defined to getDataMemberPath'
    );
    return gridArrIndex;
  }

  const relationshipKey = getRelationShipKey(entityDefinition, dataMember);
  // TODO: convert this to lowercase
  //NONPERSISTENT MUST BE CONSIDERED 1TOMANY
  const isOneToMany = ONE_TO_MANY.includes(relationshipKey);

  const updateLastIndexToZero = (_gridArrIndex) => {
    const result = [..._gridArrIndex];

    result.pop();
    result.push(0);

    return result;
  };

  if (typeof gridArrIndex === 'number') {
    return isOneToMany ? gridArrIndex : 0;
  }

  if (Array.isArray(gridArrIndex)) {
    return isOneToMany ? gridArrIndex : updateLastIndexToZero(gridArrIndex);
  }

  return;
};

const preprocessGridArrIndex = (gridArrIndex, dataMember, entityDefinition) => {
  if (_.isNil(gridArrIndex)) {
    return gridArrIndex;
  }

  if (typeof dataMember !== 'string') {
    throw new Error('DataMember should be a string on preprocessGridArrIndex()');
  }

  let fixedGridArrIndex = Array.isArray(gridArrIndex) ? [...gridArrIndex] : [gridArrIndex];

  const dataMemberLevel = dataMember.split('-').length - 1;

  if (dataMemberLevel < gridArrIndex.length) {
    fixedGridArrIndex = fixedGridArrIndex.slice(0, dataMemberLevel);
  }

  // get total of entities
  const isOneToOne = getRelationShipKey(entityDefinition, dataMember) === '1to1';

  if (dataMemberLevel > gridArrIndex.length && isOneToOne) {
    // add missing index as per scenario described on EUROPA-3087
    // we can have a third level entity field bound to a second level entity
    // i.e "V_SS_CASE-V_SS_CASE_PART_LOT-V_SS_CASE_PART_LOT_ADD_INFO.OFF_LABEL_USE" field
    // is being directly used inside a table bound to "V_SS_CASE-V_SS_CASE_PART_LOT" entity
    const missingIndex = dataMemberLevel - fixedGridArrIndex.length;

    for (let i = missingIndex; i > 0; i--) {
      fixedGridArrIndex.push(0);
    }
  }

  fixedGridArrIndex = getGridArrIndexBasedOnRelationshipKey(
    fixedGridArrIndex,
    dataMember,
    entityDefinition
  );

  return fixedGridArrIndex;
};

const getPathToDataMemberDataArray = (dataMember, gridArrIndex, entityType) => {
  const arr = dataMember.split('-');
  let xPath = ENTITY_DATA_PREFIX;

  if (dataMember === 'V_ENU_NONPERSIS_USER_DATA') {
    xPath += `${entityType}.`;
  }

  if (arr.length === 1) {
    xPath += arr[0]; // single top entity
  } else {
    for (let idx = 0; idx < arr.length; idx++) {
      if (idx === 0) {
        // top entity section
        xPath += arr[0];
      } else {
        // n level sub entity  section
        const subPath = aggregate(arr, idx);

        if (
          Array.isArray(gridArrIndex) &&
          idx < arr.length - 1 && // no [] at the end
          !_.isNil(gridArrIndex[idx - 1])
        ) {
          xPath += `.${subPath}[${gridArrIndex[idx - 1]}]`;
        } else {
          xPath += `.${subPath}`;
        }
      }
    }
  }

  return xPath;
};

const getParentGridIndexArr = (gridArrIndex) => {
  if (_.isArray(gridArrIndex)) {
    if (gridArrIndex.length < 2) {
      return;
    }
    return gridArrIndex.slice(0, gridArrIndex.length - 1);
  }
  return;
};

export const getCurrentGridIndex = (gridArrIndex) =>
  _.isArray(gridArrIndex) ? gridArrIndex[gridArrIndex.length - 1] : gridArrIndex;

/** old engine implementation of getDataPath */
export const getDataMemberPath = ({
  entityType,
  dataMember,
  gridArrIndex,
  dataField,
  entityDefinition,
}) => {
  const _gridArrIndex = preprocessGridArrIndex(gridArrIndex, dataMember, entityDefinition);
  const currentGridIndex = getCurrentGridIndex(_gridArrIndex);

  let xPath = getPathToDataMemberDataArray(
    dataMember,
    getParentGridIndexArr(_gridArrIndex),
    entityType
  );

  if (!_.isNil(dataField)) {
    if (dataMember.split('-').length === 1 || dataMember === 'V_ENU_NONPERSIS_USER_DATA') {
      // top entity
      xPath += `.${dataField}`;
    } else {
      xPath += `[${!_.isNil(currentGridIndex) ? currentGridIndex : 0}].${dataField}`;
    }
  } else {
    // dataMember should not be top entity for rowCounts()
    if (dataMember.split('-').length > 1 && !_.isNil(currentGridIndex)) {
      xPath += `[${currentGridIndex}]`;
    }
  }

  return xPath;
};

export const getFilterValue = (type, value, ssContext) => {
  const { data, entityType, gridArrIndex, entityDefinition } = ssContext;
  let fValue = value;

  switch (type) {
    case 'formula':
      fValue = evalSSEexpression(value, ssContext);
      break;

    case 'column': {
      const [srcDataMember, srcDataField] = value.split('.');
      const srcPath = getDataMemberPath({
        entityType,
        dataMember: srcDataMember,
        gridArrIndex: getValueIndex(gridArrIndex, srcDataMember, entityDefinition),
        dataField: srcDataField,
        entityDefinition,
      });
      fValue = _.get(data, srcPath);
      break;
    }

    case 'datasource':
    case 'number':
    case 'boolean':
    case 'text':
    case 'date':
      fValue = value;
      break;

    default:
      // eslint-disable-next-line no-console
      console.log(`getFilterValue(): type has no case ${type}`);
  }

  return fValue;
};

export const getSupportDataRequestFilter = (dataSourceFilter, ssContext) => {
  const supportDataRequestFilter = dataSourceFilter
    ? dataSourceFilter.map((f) => ({
        openGroup: f.OpenGroup,
        leftType: f.LeftType,
        leftValue: getFilterValue(f.LeftType, f.LeftValue, ssContext),
        op: f.Operator || f.SqlOperator,
        rightType: f.RightType,
        rightValue: getFilterValue(f.RightType, f.RightValue, ssContext),
        closeGroup: f.CloseGroup,
        relationalOp: f.RelationalOperator,
      }))
    : [];

  return supportDataRequestFilter;
};

const fixedParseFloat = (decimalScale, str) => Number(parseFloat(str).toFixed(decimalScale));

export const value2valueWithType = (fieldData, dataType, formatType, defaultValue) => {
  // Fixes NULLs as empty strings issue
  if ((typeof fieldData === 'string' && fieldData.trim() === '') || fieldData === null) {
    return defaultValue || null;
  }

  let typedValue = fieldData; // default

  switch (dataType) {
    case 'System.Byte':
    case 'System.Boolean':
      if (_.isBoolean(fieldData)) {
        typedValue = fieldData;
      } else if (_.isString(fieldData)) {
        switch (fieldData.toLowerCase()) {
          case '1':
          case 'true':
            typedValue = true;
            break;

          default:
            typedValue = false;
        }
      }
      break;

    case 'System.Int32':
      typedValue = parseInt(fieldData);
      break;

    case 'System.Decimal':
      typedValue = fixedParseFloat(getDecimalScale(dataType, formatType), fieldData);
      break;

    case 'System.String':
      // null -> '' (empty string)
      if (_.isNil(fieldData)) {
        typedValue = '';
      } else if (typeof fieldData !== 'string') {
        typedValue = fieldData.toString();
      }
      break;

    case 'System.DateTime':
      typedValue = new Date(fieldData);
      break;

    default:
    // do nothing
  }

  return typedValue;
};

export const getGridArrIndexBasedOnRequiredLength = (entityName, parentIndex) => {
  if (!entityName) {
    return parentIndex;
  }

  const entityNameDimension = entityName.split('-').length;
  const requiredIndexLength = entityNameDimension - 1;

  if (requiredIndexLength < parentIndex.length) {
    const isRootEntity = entityNameDimension === 1;
    return isRootEntity ? [0] : parentIndex.slice(0, -1);
  }

  if (requiredIndexLength > parentIndex.length) {
    const depthCount = requiredIndexLength - parentIndex.length;
    return [...parentIndex, ...Array(depthCount).fill(0)];
  }

  return parentIndex;
};

export const shouldAddDepthToGridArrIndex = (entityName, index) => {
  const entityNameDimension = entityName.split('-').length;
  const requiredIndexLength = entityNameDimension - 1;

  return requiredIndexLength > index.length;
};

export const createGridColumns = (entityDefinition, translations) => (model) => ({
  key: model.controlId,
  columnKey: model.controlId,
  accessor: (model.fieldName || model.childFieldName) ?? '',
  dataType: getPropType(
    entityDefinition,
    `${model.entityName || model.childEntityName}.${model.fieldName || model.childFieldName}`,
    'DataType'
  ),
  width: model.headerText === 'txtDueDateInBusinessDays' ? 120 : null,
  header: model.headerText
    ? translate(translations, model.headerText)
    : getPropTranslation(
        entityDefinition,
        translations,
        `${model.entityName}.${model.fieldName}`
      ) ??
      model.label ??
      '',
  childControlType: model.childControlType,
  hasDataSourceChild: model.hasDataSourceChild,
});

export const getEntityNameForZoom = (formModel, parentId) => {
  const parentChildren = formModel[parentId].childrenNodes;

  const relatedTextBoxId = parentChildren.find(
    (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.TEXT_BOX
  );

  return formModel[relatedTextBoxId]?.entityName;
};

export const getAccordionPaddings = ({ isDetailWithoutHeader, isMobileDevice, controlType }) => {
  const paddings = {
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: isMobileDevice ? 24 : 32,
  };

  if (isDetailWithoutHeader) {
    return paddings;
  }

  if (isMobileDevice) {
    if ([SS_CONTROL_TYPE.PANEL, SS_CONTROL_TYPE.REPEATING_PANEL].includes(controlType)) {
      paddings.paddingLeft = 8;
      paddings.paddingRight = 8;
      return paddings;
    }

    return paddings; // grid and repeating panel section
  }

  paddings.paddingLeft = controlType === SS_CONTROL_TYPE.GRID ? 14 : 32;
  return paddings;
};

/**
 *
 * convert child entities to arrays in case one entry (n-level nesting is supported)
 * e.g. V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_ACTION.V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC
 *
 * does recursion to support n-level nesting
 *
 * @param entity
 * @param entityDefinition
 * @param dataMemberPrefix is
 * V_SS_CAPA for V_SS_CAPA-V_SS_CAPA_ACTION
 * V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_ACTION for V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_ACTION.V_SS_CAPA-V_SS_CAPA_ACTION-V_SS_CAPA_ACTION_RC
 *
 */
const preprocessArrays = (entity, entityDefinition, dataMemberPrefix) => {
  Object.entries(entity).forEach(([propName, propValue]) => {
    // check if sub-entity (e.g. V_SS_CAPA-V_SS_CAPA_POLICY)
    // if prop has prop RelationShipKey in entity definition -> it's sub-entity -> toArray()
    // e.g. V_SS_CAPA.V_SS_CAPA-V_SS_CAPA_POLICY.properties.RelationShipKey
    if (
      _.get(entityDefinition, `${dataMemberPrefix}.${propName}.properties.RelationShipKey`) &&
      propName !== 'V_ENU_NONPERSIS_USER_DATA'
    ) {
      entity[propName] = toArray(propValue);
      entity[propName].forEach((subEntity) =>
        preprocessArrays(subEntity, entityDefinition, `${dataMemberPrefix}.${propName}`)
      );
    }
  });
};

function validateField(data, columnName, columnProperties, entityDataPathArr) {
  entityDataPathArr.forEach((calculatedEntityDataPath) => {
    const fieldDataPath = `${calculatedEntityDataPath}.${columnName}`;
    const fieldData = _.get(data, fieldDataPath);

    // no data -> nothing to fix
    if (_.isNil(fieldData)) {
      return;
    }

    _.set(
      data,
      fieldDataPath,
      value2valueWithType(fieldData, columnProperties.DataType, columnProperties.FormatType)
    );
  });
}

function validateEntity(data, entityDefinitionNode) {
  const { columns } = entityDefinitionNode;
  const entityProperties = entityDefinitionNode.properties;

  Object.entries(columns).forEach(([columnName, params]) => {
    const entityDataPathArr = getEntityDataPathArr(entityProperties.EntityDataPath, data);
    // validate if data exist for 'entityProperties.EntityDataPath' in data model
    if (entityDataPathArr.length > 0) {
      validateField(data, columnName, params.properties, entityDataPathArr);
    }
  });

  // validate child entities
  Object.entries(entityDefinitionNode).forEach(([key, node]) => {
    if (!['columns', 'properties'].includes(key)) {
      validateEntity(data, node); // child entities
    }
  });
}

/**
 *
 * @param data
 * @param entityDefinition
 */
function setDataTypesByEntityDefinition(data, entityDefinition) {
  // eslint-disable-next-line no-unused-vars
  Object.entries(entityDefinition).forEach(([key, entityDefinitionNode]) => {
    validateEntity(data, entityDefinitionNode);
  });
}

// convert child entities to arrays in case one entry
// child entities are used in Grids & Repeating Panels and supposed to be arrays
// (e.g. update for V_SS_CAPA-V_SS_CAPA_ROOT_CAUSE:  {} => [{}] )
export const preprocessEntityData = (data, entityDefinition, entityType) => {
  const topEntity = _.get(data, `${ENTITY_DATA_PREFIX}${entityType}`);
  if (_.isNil(topEntity)) {
    return data;
  }
  preprocessArrays(topEntity, entityDefinition, entityType);

  // apply data type corrections based on entity definition
  setDataTypesByEntityDefinition(_.get(data, 'entityDataStore.entityData'), entityDefinition);

  if (data.originalEntityDataStore) {
    const originalTopEntity = _.get(data, `originalEntityDataStore.entityData.${entityType}`);
    preprocessArrays(originalTopEntity, entityDefinition, entityType);
    setDataTypesByEntityDefinition(
      _.get(data, 'originalEntityDataStore.entityData'),
      entityDefinition
    );
  }

  return data;
};
